import {
  Component,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { SotiPopupTrigger } from '@soti/popup';
import { OperatingMode, DrawingMode, AnnotationTool } from '../../services/remote-control.interfaces';
import { RemoteControlAnnotationsService } from '../../services/annotations.service';
import { RemoteControlSessionService } from '../../services/session.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { XsI18nPipe } from '@nx-fe/pipes';
import { rgb2hex } from 'xsmodules/remote-control/services/color';

@Component({
  selector: 'app-remote-control-annotations-toolbar',
  templateUrl: './annotations-toolbar.component.html',
  styleUrls: ['./annotations-toolbar.component.scss'],
})
export class RemoteControlAnnotationsToolbarComponent implements AfterViewInit, OnDestroy {
  private readonly showIntroPromptKey = 'rcAnnotationsShowIntroPrompt';

  private _isDisabled: boolean;

  public readonly strokeWidthMin = 5;
  public readonly strokeWidthMax = 100;
  public readonly strokeWidthStep = 5;

  @ViewChild('intoPopupTrigger', { static: false }) introPopup: SotiPopupTrigger;
  @ViewChild('colorPopupTrigger', { static: false }) colorPopupTrigger: SotiPopupTrigger;
  @ViewChild('strokeWidthPopupTrigger', { static: false }) strokeWidthPopupTrigger: SotiPopupTrigger;
  @ViewChild('operatingModeTrigger', { static: false }) operatingModeTrigger: SotiPopupTrigger;

  @Input()
  public get isDisabled(): boolean {
    return this.annotationState.annotationPaused || this._isDisabled;
  }
  public set isDisabled(value: boolean) {
    this._isDisabled = value;
  }

  // Communicates changes to the extended toolbar
  // whether this toolbar allows extended toolbar
  // to be minimized
  @Output()
  public canMinimize = new EventEmitter<boolean>();

  // Tracks component destruction to cancel all observable subscriptions
  private onDestroy = new Subject<void>();

  public get isColorSelectionDisabled(): boolean {
    return this.annotationState.annotationTool !== 'pen' || this.isDisabled;
  }

  public get isStrokeWidthSelectionDisabled(): boolean {
    return (
      !(this.annotationState.annotationTool === 'pen' || this.annotationState.annotationTool === 'eraser') ||
      this.isDisabled
    );
  }

  public get colorPalette(): string[] {
    return this.rcAnnotationsService.colorPalette;
  }

  public get isAnnotationPaused(): boolean {
    return this.annotationState.annotationPaused;
  }

  public get isAnnotating(): boolean {
    return this.annotationState.isAnnotating;
  }

  public get operatingMode(): OperatingMode {
    return this.annotationState.operatingMode;
  }

  public get drawingMode(): DrawingMode {
    return this.annotationState.drawingMode;
  }

  public get annotationTool(): AnnotationTool {
    return this.annotationState.annotationTool;
  }

  public set annotationTool(value: AnnotationTool) {
    if (!this.isDisabled) {
      this.rcAnnotationsService.setAnnotationTool(value);
    }
  }

  public get strokeColor(): string {
    return rgb2hex(this.annotationState.strokeColor);
  }

  public get strokeWidth(): number {
    return this.annotationState.strokeWidth;
  }

  public get disableEnableTooltip(): string {
    if (this.isAnnotationPaused) {
      return this.saI18nPipe.transform('annotations_Enable_Drawing');
    } else {
      return this.saI18nPipe.transform('annotations_Disable_Drawing');
    }
  }

  public get annotationWhiteboardTooltip(): string {
    if (this.operatingMode === 'whiteboard') {
      return this.saI18nPipe.transform('annotations_Switch_To_Drawing_Mode');
    } else {
      return this.saI18nPipe.transform('annotations_Switch_To_Whiteboard');
    }
  }

  public get isDrawingModeSelectionDisabled() {
    return this.isDisabled || this.operatingMode === 'whiteboard';
  }

  private get annotationState() {
    return this.rcAnnotationsService.currentState;
  }

  constructor(
    private saI18nPipe: XsI18nPipe,
    private rcAnnotationsService: RemoteControlAnnotationsService,
    sessionService: RemoteControlSessionService
  ) {
    sessionService.deviceScreenFocused.pipe(takeUntil(this.onDestroy)).subscribe(this.closeAllPopups);
  }

  public ngAfterViewInit() {
    // To avoid expression changes after check errors run asynchronously
    setTimeout(() => {
      if (this.showIntro) {
        this.canMinimize.emit(false);
        document.addEventListener('click', this.clickOutsideIntro);
        this.introPopup.open();
      } else {
        this.canMinimize.emit(true);
      }
    }, 0);
  }

  public ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  public setStrokeWidth(value: string, popupTrigger: SotiPopupTrigger) {
    if (!this.isStrokeWidthSelectionDisabled) {
      this.rcAnnotationsService.setStrokeWidth(parseInt(value)).then(() => popupTrigger.close());
    }
  }

  public setColor(color: string, popupTrigger: SotiPopupTrigger) {
    if (!this.isColorSelectionDisabled) {
      this.rcAnnotationsService.setStrokeColor(color).then(() => popupTrigger.close());
    }
  }

  public setDrawingMode(mode: DrawingMode, popupTrigger: SotiPopupTrigger) {
    if (!this.isDisabled) {
      this.rcAnnotationsService.setDrawingMode(mode).then(() => popupTrigger.close());
    }
  }

  public async resetAnnotations(clearOnly: boolean): Promise<void> {
    if (!this.isDisabled) {
      return this.rcAnnotationsService.resetAnnotations(clearOnly);
    }
  }

  public async toggleAnnotation(): Promise<void> {
    if (this.isAnnotationPaused) {
      await this.rcAnnotationsService.resumeAnnotations();
    } else {
      await this.rcAnnotationsService.pauseAnnotations();
    }
  }

  public toggleOperatingMode() {
    if (!this.isDisabled) {
      if (this.operatingMode === 'annotation') {
        this.rcAnnotationsService.setOperatingMode('whiteboard');
      } else {
        this.rcAnnotationsService.setOperatingMode('annotation');
      }
    }
  }

  public popupOpen(popupName: string) {
    switch (popupName) {
      case 'colorPickerPopup':
        this.strokeWidthPopupTrigger.close();
        this.operatingModeTrigger.close();
        break;
      case 'strokeWidthPopup':
        this.colorPopupTrigger.close();
        this.operatingModeTrigger.close();
        break;
      case 'operatingModePopup':
        this.colorPopupTrigger.close();
        this.strokeWidthPopupTrigger.close();
        break;
      default:
        break;
    }

    this.canMinimize.emit(false);
  }

  public popupClose() {
    this.canMinimize.emit(true);
  }

  public closeIntroPopup() {
    this.canMinimize.emit(true);
    this.introPopup.close();
  }

  public get showIntro(): boolean {
    if (localStorage) {
      const value = localStorage.getItem(this.showIntroPromptKey);
      if (value) {
        return value === 'true';
      }
    }
    return true;
  }

  public set showIntro(show: boolean) {
    if (localStorage) {
      localStorage.setItem(this.showIntroPromptKey, show ? 'true' : 'false');
    }
  }

  private closeAllPopups = () => {
    this.clickOutsideIntro();
    this.operatingModeTrigger.close();
    this.colorPopupTrigger.close();
    this.strokeWidthPopupTrigger.close();
    this.canMinimize.emit(true);
  };

  private clickOutsideIntro = () => {
    this.introPopup.close();
    document.removeEventListener('click', this.clickOutsideIntro);
  };
}
